import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Play, ArrowsClockwise, MagnifyingGlass, Pause, X, ArrowCounterClockwise, HardDrives } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { nasApi, fmtDate } from './nasApi';

// 「元の場所を開く」で利用者に提示するWindowsパスの割り当て。
// 例: 論理パス 品証部/過去ﾃﾞｰﾀ/2018年/a.xls → Z:\品証部\過去ﾃﾞｰﾀ\2018年\a.xls
function WindowsPathCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    nasApi.getWindowsPaths().then(setCfg).catch(() => setCfg(null));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      setCfg(await nasApi.putWindowsPaths({
        drive_prefix: cfg.drive_prefix || '',
        unc_prefix: cfg.unc_prefix || '',
        strip_prefix: cfg.strip_prefix || '',
        overrides: cfg.overrides || [],
      }));
      toast.success('Windowsパスの割り当てを保存しました');
    } catch (e) { toast.error(e?.response?.data?.detail || '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  if (!cfg) return null;
  const sample = `${(cfg.drive_prefix || '').replace(/\\$/, '')}\\品証部\\過去ﾃﾞｰﾀ\\2018年\\見積.xls`;

  return (
    <section data-testid="nas-windows-path-card">
      <h3 className="text-sm font-semibold text-zinc-800 mb-2">Windowsパスの割り当て（元の場所を開く）</h3>
      <div className="rounded-lg border border-zinc-200 p-3 space-y-2">
        <p className="text-xs text-zinc-500">
          検索結果・プレビューの「元の場所を開く」で利用者に提示するパスです。ブラウザからエクスプローラーを
          直接起動することはできないため、貼り付け用のパスをコピーします。
        </p>
        <div className="grid gap-2 sm:grid-cols-3">
          <label className="text-xs text-zinc-500">
            マップドライブ
            <Input value={cfg.drive_prefix || ''} placeholder="Z:\"
              onChange={(e) => setCfg({ ...cfg, drive_prefix: e.target.value })}
              className="mt-1 h-9 text-sm font-mono" data-testid="nas-winpath-drive" />
          </label>
          <label className="text-xs text-zinc-500">
            UNC（任意）
            <Input value={cfg.unc_prefix || ''} placeholder="\\nas\share\"
              onChange={(e) => setCfg({ ...cfg, unc_prefix: e.target.value })}
              className="mt-1 h-9 text-sm font-mono" data-testid="nas-winpath-unc" />
          </label>
          <label className="text-xs text-zinc-500">
            先頭から除く論理パス（任意）
            <Input value={cfg.strip_prefix || ''} placeholder="share"
              onChange={(e) => setCfg({ ...cfg, strip_prefix: e.target.value })}
              className="mt-1 h-9 text-sm font-mono" data-testid="nas-winpath-strip" />
          </label>
        </div>
        <p className="text-[11px] text-zinc-400 font-mono truncate" title={sample}
          data-testid="nas-winpath-sample">例: {sample}</p>
        <Button onClick={save} disabled={saving} className="h-8 text-sm"
          data-testid="nas-winpath-save">{saving ? '保存中…' : '保存'}</Button>
      </div>
    </section>
  );
}

// NAS admin — dedicated panel (does NOT reuse DmsAdminPanel.js). Manage-only.
export default function NasAdminPanel({ onFlagsChanged }) {
  const [flags, setFlags] = useState(null);
  const [labels, setLabels] = useState({});
  const [deferred, setDeferred] = useState([]);
  const [status, setStatus] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ logical_name: '', driver_type: 'local_test', agent_relative_root: '', roles: 'admin' });

  const reload = useCallback(async () => {
    try {
      const [f, s] = await Promise.all([nasApi.getFlags(), nasApi.status().catch(() => null)]);
      setFlags(f.flags); setLabels(f.labels_ja || {}); setDeferred(f.deferred || []);
      setStatus(s);
    } catch (e) { toast.error(e?.response?.data?.detail || '読み込みに失敗しました'); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const toggleFlag = async (key, val) => {
    try {
      const r = await nasApi.putFlags({ ...flags, [key]: val });
      setFlags(r.flags); if (onFlagsChanged) onFlagsChanged(r.flags);
    } catch (e) { toast.error(e?.response?.data?.detail || '変更できません'); }
  };

  const createRoot = async () => {
    if (!form.logical_name.trim()) { toast.error('名称を入力してください'); return; }
    try {
      await nasApi.createRoot({
        logical_name: form.logical_name, driver_type: form.driver_type,
        agent_relative_root: form.agent_relative_root,
        permission_roles: form.roles.split(',').map((s) => s.trim()).filter(Boolean),
        permission_dept_ids: [],
      });
      toast.success('ソースを登録しました'); setCreating(false);
      setForm({ logical_name: '', driver_type: 'local_test', agent_relative_root: '', roles: 'admin' });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || '登録できません'); }
  };

  const dryRun = async (r) => {
    try { await nasApi.dryRun(r.id); toast.success('ドライ棚卸しを開始しました'); setTimeout(reload, 1500); }
    catch (e) { toast.error(e?.response?.data?.detail || '実行できません'); }
  };
  const activate = async (r) => {
    const subtree = window.prompt('パイロットにする1つのフォルダー（NAS内の相対パス、空欄=ルート全体）', r.pilot_subtree || '');
    if (subtree === null) return;
    if (!window.confirm('このフォルダーをパイロットとして有効化し、取り込み（ファイル名・パス・メタデータのみ）を開始します。よろしいですか？')) return;
    try {
      await nasApi.activatePilot({ root_id: r.id, pilot_subtree: subtree,
        idempotency_key: `pilot-${r.id}-${Date.now()}`, confirm: true });
      toast.success('パイロットを有効化しました'); setTimeout(reload, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || '有効化できません'); }
  };
  const rescan = async (r) => { try { await nasApi.rescan(r.id); toast.success('再スキャンを開始しました'); setTimeout(reload, 1500); } catch (e) { toast.error(e?.response?.data?.detail || '実行できません'); } };
  const reconcile = async (r) => { try { await nasApi.reconcile(r.id); toast.success('突合を開始しました'); setTimeout(reload, 1500); } catch (e) { toast.error(e?.response?.data?.detail || '実行できません'); } };
  const jobAction = async (id, action) => { try { await nasApi.jobControl(id, action); setTimeout(reload, 800); } catch (e) { toast.error(e?.response?.data?.detail || '実行できません'); } };

  if (!flags) return <p className="text-sm text-zinc-400 py-6">読み込み中…</p>;
  const roots = status?.roots || [];
  const jobs = status?.jobs || [];

  return (
    <div className="space-y-6" data-testid="nas-admin-panel">
      {/* Feature flags */}
      <section>
        <h3 className="text-sm font-semibold text-zinc-800 mb-2">機能フラグ</h3>
        <div className="rounded-lg border border-zinc-200 divide-y divide-zinc-100">
          {Object.keys(flags).map((k) => {
            const isDeferred = deferred.includes(k);
            return (
              <div key={k} className="flex items-center gap-3 px-3 py-2">
                <span className="text-sm text-zinc-700 flex-1">
                  {labels[k] || k}
                  {isDeferred && <span className="ml-2 text-xs text-amber-600">未実装・有効化不可</span>}
                </span>
                <Switch checked={!!flags[k]} disabled={isDeferred}
                  onCheckedChange={(v) => toggleFlag(k, v)}
                  aria-label={`${labels[k] || k} を切り替え`} data-testid={`nas-flag-${k}`} />
              </div>
            );
          })}
        </div>
      </section>

      {/* Windows path mapping（元の場所を開く） */}
      <WindowsPathCard />

      {/* Source roots */}
      <section>
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-sm font-semibold text-zinc-800 flex-1">NASソース</h3>
          <Button variant="outline" onClick={() => setCreating((v) => !v)} className="h-8 text-sm" data-testid="nas-add-root-btn">
            <Plus size={14} className="mr-1" /> ソースを登録
          </Button>
        </div>
        {creating && (
          <div className="rounded-lg border border-teal-200 bg-teal-50/40 p-3 mb-3 space-y-2" data-testid="nas-root-form">
            <Input placeholder="表示名（例: 技術部NAS）" value={form.logical_name}
              onChange={(e) => setForm({ ...form, logical_name: e.target.value })}
              className="h-9 text-sm" data-testid="nas-root-name" />
            <div className="grid grid-cols-2 gap-2">
              <Select value={form.driver_type} onValueChange={(v) => setForm({ ...form, driver_type: v })}>
                <SelectTrigger className="h-9 text-sm" aria-label="ソース種別" data-testid="nas-root-driver"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nas_agent">NASエージェント（本番）</SelectItem>
                  <SelectItem value="local_test">テストソース（ローカル）</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder="NAS内の相対ルート（空欄可）" value={form.agent_relative_root}
                onChange={(e) => setForm({ ...form, agent_relative_root: e.target.value })}
                className="h-9 text-sm" data-testid="nas-root-relroot" />
            </div>
            <Input placeholder="閲覧を許可するロール（カンマ区切り。例: admin,manager）" value={form.roles}
              onChange={(e) => setForm({ ...form, roles: e.target.value })}
              className="h-9 text-sm" data-testid="nas-root-roles" />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setCreating(false)} className="h-8 text-sm">キャンセル</Button>
              <Button onClick={createRoot} className="h-8 text-sm bg-teal-700 hover:bg-teal-800 text-white" data-testid="nas-root-save">登録</Button>
            </div>
          </div>
        )}
        {roots.length === 0 ? (
          <p className="text-sm text-zinc-400 py-2">ソースがありません。まず登録してください。</p>
        ) : roots.map((r) => (
          <div key={r.id} className="rounded-lg border border-zinc-200 bg-white p-3 mb-2" data-testid={`nas-root-${r.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <HardDrives size={16} className="text-teal-700" />
              <span className="text-sm font-medium text-zinc-900">{r.logical_name}</span>
              {r.is_test && <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs text-violet-700">テストソース</span>}
              <span className="text-xs text-zinc-400">取り込み: {r.indexing_status}</span>
              {r.active && <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">パイロット有効</span>}
              {r.health && <span className={`text-xs ${r.health.ok ? 'text-emerald-600' : 'text-rose-600'}`}>{r.health.ok ? '接続OK' : (r.health.message || '未接続')}</span>}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              <Button variant="outline" onClick={() => dryRun(r)} className="h-8 text-xs" data-testid={`nas-dryrun-${r.id}`}>
                <MagnifyingGlass size={13} className="mr-1" /> ドライ棚卸し
              </Button>
              <Button variant="outline" onClick={() => activate(r)} className="h-8 text-xs" disabled={!r.dry_run_completed_at} data-testid={`nas-activate-${r.id}`}>
                <Play size={13} className="mr-1" /> パイロット有効化
              </Button>
              <Button variant="outline" onClick={() => rescan(r)} className="h-8 text-xs" disabled={!r.active} data-testid={`nas-rescan-${r.id}`}>
                <ArrowsClockwise size={13} className="mr-1" /> 再スキャン
              </Button>
              <Button variant="outline" onClick={() => reconcile(r)} className="h-8 text-xs" disabled={!r.active} data-testid={`nas-reconcile-${r.id}`}>
                突合
              </Button>
            </div>
          </div>
        ))}
      </section>

      {/* Jobs */}
      <section>
        <h3 className="text-sm font-semibold text-zinc-800 mb-2">ジョブ</h3>
        {jobs.length === 0 ? (
          <p className="text-sm text-zinc-400">ジョブはありません</p>
        ) : (
          <div className="space-y-1.5" data-testid="nas-jobs">
            {jobs.map((j) => (
              <div key={j.id} className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm" data-testid={`nas-job-${j.id}`}>
                <span className="font-medium text-zinc-800">{j.job_type}</span>
                <span className="text-xs text-zinc-400">{j.status}</span>
                <span className="text-xs text-zinc-400">取込 {j.progress?.indexed || 0} / 失敗 {j.progress?.failed || 0}</span>
                <span className="text-xs text-zinc-300 ml-auto">{fmtDate(j.created_at)}</span>
                {j.status === 'running' && <button type="button" onClick={() => jobAction(j.id, 'pause')} aria-label="一時停止" title="一時停止" className="h-8 w-8 flex items-center justify-center text-zinc-500 hover:bg-zinc-100 rounded" data-testid={`nas-job-pause-${j.id}`}><Pause size={14} /></button>}
                {j.status === 'paused' && <button type="button" onClick={() => jobAction(j.id, 'resume')} aria-label="再開" title="再開" className="h-8 w-8 flex items-center justify-center text-zinc-500 hover:bg-zinc-100 rounded" data-testid={`nas-job-resume-${j.id}`}><Play size={14} /></button>}
                {j.status === 'failed' && <button type="button" onClick={() => jobAction(j.id, 'retry')} aria-label="再試行" title="再試行" className="h-8 w-8 flex items-center justify-center text-zinc-500 hover:bg-zinc-100 rounded" data-testid={`nas-job-retry-${j.id}`}><ArrowCounterClockwise size={14} /></button>}
                {(j.status === 'running' || j.status === 'paused') && <button type="button" onClick={() => jobAction(j.id, 'cancel')} aria-label="中止" title="中止" className="h-8 w-8 flex items-center justify-center text-rose-500 hover:bg-rose-50 rounded" data-testid={`nas-job-cancel-${j.id}`}><X size={14} /></button>}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
